import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { BUDGET_BULK_EXPORT_ORG_LIMIT, BudgetService } from "../budget/budget.service";
import { BudgetSpreadsheetUtil } from "../budget/budget.spreadsheet.util";
import { OrganizationExport } from "./organization.export.types";

export const OrganizationExportBudgetHandler: OrganizationExport.Handler = {
    kind: "budget",
    run: runBudgetOrganizationExport,
};

export const ORGANIZATION_EXPORT_HANDLERS: Record<OrganizationExport.Kind, OrganizationExport.Handler> = {
    budget: OrganizationExportBudgetHandler,
};

async function runBudgetOrganizationExport(
    context: OrganizationExport.Context,
    budgetOptions: OrganizationExport.BudgetOptions | undefined,
): Promise<void> {
    const options = { ...OrganizationExport.DEFAULT_BUDGET_OPTIONS, ...budgetOptions };
    if (!options.includeOrgBudgetSummary && !options.includeBudgetPlans && !options.includeLineItems) {
        AlertService.alert("Select at least one budget section to export.");
        return;
    }

    const flattenOptions = { budget: options };

    if (context.mode === "singleOrg") {
        const organizationId = context.organizationId;
        if (!organizationId) {
            AlertService.alert("Organization is required for export.");
            return;
        }
        const [exportPayload, exportError] = await S25Util.Maybe(
            BudgetService.getOrgBudgetExport(organizationId, context.fiscalYearId),
        );
        if (exportError) {
            AlertService.alert(`Export failed: ${S25Util.errorText(exportError)}`);
            return;
        }
        const rows = BudgetSpreadsheetUtil.flattenExport(exportPayload, flattenOptions);
        const organizationName = exportPayload.organizationName ?? context.organizationName ?? `org-${organizationId}`;
        await BudgetSpreadsheetUtil.downloadExport(
            rows,
            BudgetSpreadsheetUtil.buildExportFileName(organizationName),
            flattenOptions,
        );
        return;
    }

    if (context.mode === "bulkByFiscalYear") {
        const fiscalYear = context.fiscalYear;
        if (!fiscalYear) {
            AlertService.alert("Fiscal year is required for export.");
            return;
        }
        const [bulkExport, exportError] = await S25Util.Maybe(
            BudgetService.getBulkBudgetExportByFiscalYear(fiscalYear, context.fiscalYearId),
        );
        if (exportError) {
            AlertService.alert(`Export failed: ${S25Util.errorText(exportError)}`);
            return;
        }
        const rows = BudgetSpreadsheetUtil.flattenBulkExport(bulkExport?.exports ?? [], flattenOptions);
        await BudgetSpreadsheetUtil.downloadExport(
            rows,
            BudgetSpreadsheetUtil.buildBulkExportFileName(fiscalYear),
            flattenOptions,
        );
        return;
    }

    const organizationIds = context.organizationIds ?? [];
    if (!organizationIds.length) {
        AlertService.alert("Select at least one organization to export.");
        return;
    }
    if (organizationIds.length > BUDGET_BULK_EXPORT_ORG_LIMIT) {
        AlertService.alert(
            `You can export budgets for at most ${BUDGET_BULK_EXPORT_ORG_LIMIT} organizations at a time.`,
        );
        return;
    }
    const [bulkExport, exportError] = await S25Util.Maybe(
        BudgetService.postBulkBudgetExport(organizationIds, context.fiscalYearId),
    );
    if (exportError) {
        AlertService.alert(`Export failed: ${S25Util.errorText(exportError)}`);
        return;
    }
    const rows = BudgetSpreadsheetUtil.flattenBulkExport(bulkExport?.exports ?? [], flattenOptions);
    await BudgetSpreadsheetUtil.downloadExport(
        rows,
        BudgetSpreadsheetUtil.buildBulkExportFileName(`selected-orgs-${organizationIds.length}`),
        flattenOptions,
    );
}
